import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, TOKEN_KEY, DEMO_TOKEN_KEY, authToken, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = anon
  const [loading, setLoading] = useState(true);

  const bootstrap = useCallback(async () => {
    const token = authToken();
    if (!token) { setUser(null); setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      if (sessionStorage.getItem(DEMO_TOKEN_KEY)) sessionStorage.removeItem(DEMO_TOKEN_KEY);
      else localStorage.removeItem(TOKEN_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const login = async (email, password, totp_code) => {
    try {
      const payload = { email, password };
      if (totp_code) payload.totp_code = totp_code;
      const { data } = await api.post("/auth/login", payload);
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // 403 "2FA_REQUIRED" — server is asking for a TOTP code
      if (detail === "2FA_REQUIRED") return { ok: false, needs_2fa: true };
      return { ok: false, error: formatApiError(detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e?.response?.data?.detail) || e.message };
    }
  };

  /* A sign-up endpoint elsewhere already issued the session — just take it on. */
  const adopt = (token, nextUser) => {
    localStorage.setItem(TOKEN_KEY, token);
    setUser(nextUser);
  };

  const logout = async () => {
    // Clear localStorage first so any in-flight requests don't carry the old token,
    // then ask the backend to clear the httpOnly cookie. The POST is fire-and-forget
    // — if it fails (e.g. network drop) the local state is still cleaned up.
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(DEMO_TOKEN_KEY);
    setUser(null);
    try { await api.post("/auth/logout"); } catch (_) {}
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, adopt, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export function roleHome(role) {
  if (role === "admin") return "/choose";
  if (role === "affiliate") return "/affiliate";
  if (role === "publisher") return "/publisher";
  if (role === "lender") return "/lender";
  if (role === "referrer") return "/introducer";
  return "/dashboard";
}
